// src/components/blog/ShareButtons.tsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Share2, Linkedin, Twitter, Facebook, Link as LinkIcon, Check } from 'lucide-react';

interface ShareButtonsProps {
  /** The title of the article to be shared. */
  title: string;
  /** Optional: The full URL to share. If not provided, it will use the current window location. */
  url?: string;
  /** Optional: A short summary for sharing (used by LinkedIn). */
  summary?: string;
  /** Optional: Twitter handle to mention (without the @). */
  via?: string;
}

export const ShareButtons: React.FC<ShareButtonsProps> = ({
  title,
  url,
  summary,
  via = "DopomogAI", // Your default Twitter handle
}) => {
  const { t } = useTranslation('blog');
  const [pageUrl, setPageUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // This effect ensures the component uses the correct URL even during server-side rendering or pre-rendering.
    // It sets the URL only on the client-side where `window.location.href` is available.
    setPageUrl(url || window.location.href);
  }, [url]);

  const encodedUrl = encodeURIComponent(pageUrl);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = {
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}&summary=${encodeURIComponent(summary || '')}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}&via=${via}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(pageUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };
  
  const iconButtonClass = "flex items-center justify-center w-12 h-12 rounded-lg border-2 border-black transition-all duration-150 ease-out transform hover:-translate-y-1 active:translate-y-0";

  return (
    <div className="flex flex-wrap items-center gap-3">
       <div className="flex items-center gap-2 text-lg font-bold text-gray-900 mr-2">
           <Share2 size={22} />
           <span>{t('sections.shareTitle', 'Share this article')}:</span>
       </div>
      
      {/* LinkedIn */}
      <a
        href={shareLinks.linkedin}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('share.linkedinAria', 'Share on LinkedIn')}
        className={`${iconButtonClass} bg-sky-100 text-sky-700 hover:bg-sky-200`}
      >
        <Linkedin size={20} />
      </a>

      {/* Twitter */}
      <a
        href={shareLinks.twitter}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('share.twitterAria', 'Share on Twitter')}
        className={`${iconButtonClass} bg-slate-200 text-slate-800 hover:bg-slate-300`}
      >
        <Twitter size={20} />
      </a>
      
      {/* Facebook */}
       <a
        href={shareLinks.facebook}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('share.facebookAria', 'Share on Facebook')}
        className={`${iconButtonClass} bg-blue-100 text-blue-700 hover:bg-blue-200`}
      >
        <Facebook size={20} />
      </a>

      {/* Copy Link Button */}
      <button
        onClick={handleCopyLink}
        aria-label={t('share.copyLinkAria', 'Copy link to clipboard')}
        className={`${iconButtonClass} ${copied ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
      >
        {copied ? <Check size={20} /> : <LinkIcon size={20} />}
      </button>
      {copied && (
         <span className="text-sm font-semibold text-emerald-600 animate-pulse">
            {t('share.copied', 'Copied!')}
         </span>
      )}
    </div>
  );
};